import { useCallback, useEffect, useState } from "react";
import { ClubService } from "../../../../services/clubService";
import { EventService } from "../../../../services/eventService";
import { isEventUpcoming } from "../../lib/format";
import { useToast } from "../../components/Toast";
import { msg } from "./manageUtils";

/** Share of past-event RSVPs that checked in, or null when it can't be computed. */
function checkInRate(events) {
  let going = 0;
  let checked = 0;
  for (const e of events) {
    if (isEventUpcoming(e)) continue;
    const n = Number(e.attendee_count) || 0;
    if (n <= 0) continue;
    going += n;
    checked += Math.min(n, Number(e.checkIn_count) || 0);
  }
  return going > 0 ? Math.round((checked / going) * 100) : null;
}

/** Overview tab data + actions (AdminScreen loadOverviewData :587, Club of the Week :884-935, :2832). */
export default function useOverviewData({ club, onAddPhotos, onTab, onPatch }) {
  const toast = useToast();
  const [state, setState] = useState({ loading: true, error: null, events: [], members: null, pending: null });
  const [reload, setReload] = useState(0);
  const [picker, setPicker] = useState(false);
  const [confirmEvent, setConfirmEvent] = useState(null);
  const [broadcasting, setBroadcasting] = useState(false);
  const [addingPhotos, setAddingPhotos] = useState(false);
  const clubId = club.id;

  useEffect(() => {
    let live = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    (async () => {
      const [ev, mem, apps] = await Promise.all([
        EventService.getEvents({ clubId }, { userClubIds: [clubId] }),
        ClubService.getClubMembers(clubId),
        ClubService.getClubApplications(clubId),
      ]);
      if (!live) return;
      if (ev.error && mem.error) { setState({ loading: false, error: ev.error, events: [], members: null, pending: null }); return; }
      setState({
        loading: false,
        error: null,
        events: ev.error ? [] : ev.data || [],
        members: mem.error ? null : (mem.data || []).length,
        pending: apps.error ? null : (apps.data || []).length,
        eventsFailed: !!ev.error,
      });
    })();
    return () => { live = false; };
  }, [clubId, reload]);

  const upcoming = state.events.filter((e) => isEventUpcoming(e));
  const rate = checkInRate(state.events);

  const start = () => {
    if (upcoming.length === 0) {
      toast("Add an event before announcing. The broadcast highlights your next event to students.", "alert");
    } else if (upcoming.length === 1) {
      setConfirmEvent(upcoming[0]);
    } else {
      setPicker(true);
    }
  };

  const broadcast = async () => {
    const event = confirmEvent;
    setBroadcasting(true);
    try {
      const { error } = await ClubService.broadcastClubOfTheWeek(club.id, event.id);
      if (error) { toast(msg(error, "Couldn't send announcement. Please try again in a moment."), "alert"); return; }
      onPatch({ featured_broadcast_sent: true });
      toast("Announcement sent! VibeLink students have been notified.");
    } finally {
      setBroadcasting(false);
      setConfirmEvent(null);
      setPicker(false);
    }
  };

  const tile = useCallback(async (k) => {
    if (k !== "photos") { onTab(k); return; }
    setAddingPhotos(true);
    try { await onAddPhotos(); } finally { setAddingPhotos(false); }
  }, [onTab, onAddPhotos]);
  return {
    state, upcoming, rate, start, broadcast, tile, picker, setPicker,
    confirmEvent, setConfirmEvent, broadcasting, addingPhotos, retry: () => setReload((n) => n + 1),
  };
}
